import {
  isContactWorkflow,
  isUserWorkflow,
} from "../onboarding/workflowParticipants";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import { Add, AccountTree, ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, Navigate, useNavigate } from "react-router-dom";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useAuthenticationStore } from "../../../stores/auth";

export default function OnboardingWorkflowsPage() {
  const api = useApiClient().marketing;
  const navigate = useNavigate();
  const user = useAuthenticationStore((state) => state.user);
  const isSuperuser = user?.info?.activeContext?.contextType === "superuser";
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createOutreach, setCreateOutreach] = useState(false);
  const [workflowTypes, setWorkflowTypes] = useState([]);
  const [newWorkflow, setNewWorkflow] = useState({ name: "", type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getManagement();
      setWorkflows(result.marketingWorkflows || []);
      setWorkflowTypes(result.marketingWorkflowTypes || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const availableTypes = workflowTypes.filter(
    (type) =>
      type.code === "general_outreach" ||
      !workflows.some((workflow) => workflow.workflowType === type.code),
  );
  const openCreate = (outreach) => {
    setCreateOutreach(outreach);
    const type = availableTypes.find((item) =>
      outreach ? isContactWorkflow(item) : isUserWorkflow(item),
    );
    setNewWorkflow({ name: "", type: type?.code || "" });
    setCreateOpen(true);
  };
  const createWorkflow = async () => {
    setCreating(true);
    setError("");
    try {
      const result = await api.createWorkflow({
        workflowType: newWorkflow.type,
        name: newWorkflow.name,
        description: "",
        isEnabled: false,
        sendTimezone: "Europe/London",
      });
      if (!result.success) {
        enqueueSnackbar(
          result.errors?.map((item) => item.message).join(" ") ||
            "The workflow could not be created.",
          { variant: "error" },
        );
        return;
      }
      enqueueSnackbar("Workflow created.", { variant: "success" });
      navigate(
        `/superuser/marketing/workflows/${encodeURIComponent(result.workflow.id)}`,
      );
    } catch (requestError) {
      enqueueSnackbar(requestError.message, { variant: "error" });
    } finally {
      setCreating(false);
    }
  };

  if (!isSuperuser) return <Navigate to="/" replace />;

  return (
    <FruitifyContainer>
      <Stack spacing={3}>
        <Button
          component={RouterLink}
          to="/superuser/marketing"
          startIcon={<ArrowBack />}
          sx={{ alignSelf: "flex-start" }}
        >
          Back to marketing
        </Button>
        {error && <Alert severity="error">{error}</Alert>}
        {!loading &&
          workflows.some(
            (workflow) =>
              !isContactWorkflow(workflow) && !isUserWorkflow(workflow),
          ) && (
            <Alert severity="info">
              Some workflow options are unavailable. Please reload the page.
            </Alert>
          )}
        {loading ? (
          <Box textAlign="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          [false, true].map((outreach) => {
            const sectionWorkflows = workflows.filter((workflow) =>
              outreach ? isContactWorkflow(workflow) : isUserWorkflow(workflow),
            );
            const canCreate = availableTypes.some((type) =>
              outreach ? isContactWorkflow(type) : isUserWorkflow(type),
            );
            const headingId = outreach
              ? "outreach-workflows"
              : "customer-workflows";
            return (
              <Stack
                component="section"
                aria-labelledby={headingId}
                key={headingId}
                spacing={2}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography id={headingId} variant="h4">
                      {outreach ? "Outreach workflows" : "Customer workflows"}
                    </Typography>
                    <Typography color="text.secondary">
                      {outreach
                        ? "Manage journeys for verified outreach contacts."
                        : "Manage automated journeys for existing Fruitify users."}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => openCreate(outreach)}
                    disabled={creating || !canCreate}
                  >
                    {outreach
                      ? "Create outreach workflow"
                      : "Create customer workflow"}
                  </Button>
                </Stack>
                {sectionWorkflows.length === 0 ? (
                  <Paper sx={{ p: 5, textAlign: "center" }}>
                    <AccountTree color="primary" sx={{ fontSize: 52 }} />
                    <Typography variant="h5" mt={1}>
                      {outreach
                        ? "Create your outreach journey"
                        : "Create your onboarding journey"}
                    </Typography>
                    <Typography color="text.secondary" my={2}>
                      Start with an empty draft, then add stages and email
                      content.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {sectionWorkflows.map((workflow) => (
                      <Grid key={workflow.id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Paper
                          component="article"
                          variant="outlined"
                          sx={{ p: 3, height: "100%" }}
                        >
                          <Stack spacing={2} height="100%">
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <Box>
                                <Typography
                                  variant="overline"
                                  color="text.secondary"
                                >
                                  {workflow.workflowTypeLabel}
                                </Typography>
                                <Typography variant="h6">
                                  {workflow.name}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                color={
                                  workflow.isEnabled ? "success" : "default"
                                }
                                label={workflow.isEnabled ? "Active" : "Paused"}
                              />
                            </Stack>
                            <Typography color="text.secondary" flex={1}>
                              {workflow.description ||
                                "No description has been added."}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {[
                                ["LIVE", "live version"],
                                ["DRAFT", "draft"],
                                ["ARCHIVED", "archived version"],
                              ].map(([status, label]) => {
                                const count = (workflow.versions || []).filter(
                                  (version) => version.status === status,
                                ).length;
                                return (
                                  <Chip
                                    key={status}
                                    size="small"
                                    variant="outlined"
                                    label={`${count} ${label}${count === 1 ? "" : "s"}`}
                                  />
                                );
                              })}
                            </Stack>
                            <Button
                              endIcon={<ArrowForward />}
                              onClick={() =>
                                navigate(
                                  `/superuser/marketing/workflows/${encodeURIComponent(workflow.id)}`,
                                )
                              }
                            >
                              Open workflow
                            </Button>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Stack>
            );
          })
        )}
        <Dialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          fullWidth
        >
          <DialogTitle>Create workflow</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                select
                label="Workflow type"
                value={newWorkflow.type}
                onChange={(event) =>
                  setNewWorkflow((value) => ({
                    ...value,
                    type: event.target.value,
                  }))
                }
              >
                {availableTypes
                  .filter((type) =>
                    createOutreach
                      ? isContactWorkflow(type)
                      : isUserWorkflow(type),
                  )
                  .map((type) => (
                    <MenuItem key={type.code} value={type.code}>
                      {type.label}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                label="Workflow name"
                value={newWorkflow.name}
                onChange={(event) =>
                  setNewWorkflow((value) => ({
                    ...value,
                    name: event.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={createWorkflow}
              disabled={
                creating || !newWorkflow.type || !newWorkflow.name.trim()
              }
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </FruitifyContainer>
  );
}
